import { creditCheckTranslations } from '@/helpers/creditCheckTranslations';
import { useTranslation } from '@/hooks/useTranslation';
import { Box, FormikSelectInputNative, FormikTextInput, Toggle } from '@palmetto/palmetto-components';
import { Field, FormikErrors, useFormikContext } from 'formik';
import capitalize from 'lodash/capitalize';
import { useEffect, useState } from 'react';

import { Locale } from 'types';

import { getStateOptions } from '../../helpers/getStateOptions';
import { AddressFieldNames, setAddressDetails } from '../../helpers/setAddressDetails';

import FormikAddressInput from './FormikAddressInput';

type SetFieldValue = (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<unknown>>;

type AddressInheritToggleConfig = {
  fieldName: string;
  label: string;
  translationKey: string;
};

const concatWithPrefix = (prefix: string | undefined, nameOrId: string) => {
  return prefix ? prefix + capitalize(nameOrId) : nameOrId;
};

/**
 * This component is used both within our application and via invite token
 * Any API queries made here or in child components need to use the invite token otherwise it won't work accessing outside of our app
 */
export const AddressFormFields = ({
  isSubmitting,
  prefix,
  showLatLon = false,
  showAddress2 = false,
  isAddressRequired = true,
  disabledFields = [],
  isLatLonRequired = false,
  addressInheritToggleConfig,
}: {
  isSubmitting: boolean;
  prefix?: string | undefined;
  showLatLon?: boolean;
  showAddress2?: boolean;
  isAddressRequired?: boolean;
  disabledFields?: string[];
  isLatLonRequired?: boolean;
  addressInheritToggleConfig?: AddressInheritToggleConfig;
}) => {
  const { setFieldValue, values }: { setFieldValue: SetFieldValue; values: any } = useFormikContext();
  const { translate } = useTranslation({ useAccountPreference: true });
  const address1FieldName = concatWithPrefix(prefix, 'address1');
  const address2FieldName = concatWithPrefix(prefix, 'address2');
  const cityFieldName = concatWithPrefix(prefix, 'city');
  const stateFieldName = concatWithPrefix(prefix, 'state');
  const zipFieldName = concatWithPrefix(prefix, 'zip');
  const latFieldName = concatWithPrefix(prefix, 'lat');
  const lonFieldName = concatWithPrefix(prefix, 'lon');
  const [showAddressFields, setShowAddressFields] = useState(!addressInheritToggleConfig);
  const preferredLanguage: Locale = values?.language || Locale.en;

  useEffect(() => {
    if (addressInheritToggleConfig && values[addressInheritToggleConfig.fieldName]) {
      const thisAddressWillBeInherited = values[addressInheritToggleConfig.fieldName];
      setShowAddressFields(!thisAddressWillBeInherited);
    } else {
      setShowAddressFields(true);
    }
  }, [addressInheritToggleConfig, values]);

  const onInheritAddressToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (addressInheritToggleConfig) {
      setFieldValue(addressInheritToggleConfig.fieldName, event.target.checked);
    }
    setShowAddressFields(!event.target.checked);
  };

  const addressFieldNames: AddressFieldNames = {
    address1FieldName,
    address2FieldName,
    cityFieldName,
    stateFieldName,
    zipFieldName,
    latFieldName,
    lonFieldName,
  };
  return (
    <Box childGap={{ base: 'lg', desktop: 'xl' }}>
      {addressInheritToggleConfig && (
        <Toggle
          id={`${prefix ? prefix : ''}AddressToggle`}
          label={
            translate(addressInheritToggleConfig.translationKey, creditCheckTranslations, preferredLanguage) ||
            addressInheritToggleConfig.label
          }
          isChecked={values[addressInheritToggleConfig.fieldName]}
          onChange={onInheritAddressToggleChange}
        />
      )}
      {showAddressFields && (
        <Box childGap={{ base: 'lg', desktop: 'xl' }}>
          <Box
            direction={{
              base: 'column',
              tablet: 'row',
            }}
            childGap={{ base: 'lg', desktop: 'xl' }}
          >
            <Field
              type="text"
              label={
                showAddress2
                  ? translate('address1', creditCheckTranslations, preferredLanguage)
                  : translate('address', creditCheckTranslations, preferredLanguage)
              }
              name={address1FieldName}
              id={address1FieldName}
              component={FormikAddressInput}
              onSelect={(_data: string, placeId: string) =>
                setAddressDetails(placeId, setFieldValue, addressFieldNames)
              }
              onChange={(data: string) => setFieldValue(address1FieldName, data)}
              isRequired={isAddressRequired}
              autoComplete="off"
              isDisabled={isSubmitting || disabledFields.includes(address1FieldName)}
              value={values ? (values[address1FieldName] as string) : ''}
            />
            {showAddress2 && (
              <Field
                type="text"
                label={translate('address2', creditCheckTranslations, preferredLanguage)}
                name={address2FieldName}
                id={address2FieldName}
                component={FormikTextInput}
                autoComplete="off"
                isDisabled={isSubmitting || disabledFields.includes(address2FieldName)}
              />
            )}
          </Box>
          <Box
            direction={{
              base: 'column',
              tablet: 'row',
            }}
            childGap={{ base: 'lg', desktop: 'xl' }}
          >
            <Field
              type="text"
              label={translate('city', creditCheckTranslations, preferredLanguage)}
              name={cityFieldName}
              id={cityFieldName}
              component={FormikTextInput}
              isRequired={isAddressRequired}
              autoComplete="off"
              isDisabled={isSubmitting || disabledFields.includes(cityFieldName)}
            />
            <Field
              type="text"
              label={translate('state', creditCheckTranslations, preferredLanguage)}
              name={stateFieldName}
              id={stateFieldName}
              options={getStateOptions()}
              component={FormikSelectInputNative}
              isRequired={isAddressRequired}
              autoComplete="off"
              isDisabled={isSubmitting || disabledFields.includes(stateFieldName)}
            />
            <Field
              maxLength={5}
              type="text"
              label={translate('zipCode', creditCheckTranslations, preferredLanguage)}
              name={zipFieldName}
              id={zipFieldName}
              component={FormikTextInput}
              isRequired={isAddressRequired}
              autoComplete="off"
              isDisabled={isSubmitting || disabledFields.includes(zipFieldName)}
            />
          </Box>
        </Box>
      )}
      {showLatLon && (
        <Box
          direction={{
            base: 'column',
            tablet: 'row',
          }}
          childGap={{ base: 'lg', desktop: 'xl' }}
        >
          <Field
            type="number"
            label={translate('latitude', creditCheckTranslations, preferredLanguage)}
            name={latFieldName}
            id={latFieldName}
            component={FormikTextInput}
            autoComplete="off"
            isDisabled={isSubmitting || disabledFields.includes(latFieldName)}
            isRequired={isLatLonRequired}
          />
          <Field
            type="number"
            label={translate('longitude', creditCheckTranslations, preferredLanguage)}
            name={lonFieldName}
            id={lonFieldName}
            component={FormikTextInput}
            autoComplete="off"
            isDisabled={isSubmitting || disabledFields.includes(lonFieldName)}
            isRequired={isLatLonRequired}
          />
        </Box>
      )}
    </Box>
  );
};
